import { useEffect, useState } from 'react';
import { Center, Loader } from '@mantine/core';
import { useParams, useLocation } from 'react-router-dom';
import { api } from '../../api/client';
import { type OrderStatus } from './OrderStatusBar';
import OrderSetupPage from './OrderSetupPage';
import InQuotationPage from './InQuotationPage';
import OngoingOrderPage from './OngoingOrderPage';

// Any order.status that represents the "ongoing" phase (post-accept). OngoingOrderPage owns
// the UI for all of these — the current status is highlighted as the active Step row.
const ONGOING_STATUSES: ReadonlySet<OrderStatus> = new Set<OrderStatus>([
  'Order Started', 'Supplying Consumables', 'In Execution',
  'Preparing to Ship', 'Shipped', 'Delivered',
]);

export default function OrderDispatch() {
  const { id } = useParams();
  const location = useLocation();
  const [status, setStatus] = useState<OrderStatus | null>(null);

  const isNew = id === 'new' || location.pathname === '/orders/new' || location.pathname.endsWith('/orders/new/');
  const forceSetup = location.pathname.endsWith('/setup');

  useEffect(() => {
    if (isNew || !id) return;
    (async () => {
      try {
        const o = await api.get<{ status: OrderStatus }>(`/orders/${id}`);
        setStatus(o.status);
      } catch { setStatus(null); }
    })();
    // Depend on `location.key` as well so a same-path navigate() (e.g. after Start Order) still
    // refetches and lets the dispatch swap to the Ongoing phase even when pathname is identical.
  }, [id, isNew, location.pathname, location.key]);

  if (isNew) return <OrderSetupPage />;

  if (!status) return <Center p="xl"><Loader /></Center>;
  if (forceSetup || status === 'Order Set-up') return <OrderSetupPage />;
  if (status === 'In Quotation') return <InQuotationPage />;
  if (ONGOING_STATUSES.has(status)) return <OngoingOrderPage />;

  // Fallback — should be unreachable given ORDER_STATUSES is a closed set, but keep a safe
  // degradation that doesn't crash if a new status is added without updating the dispatch.
  return (
    <Center p="xl">
      <Loader />
    </Center>
  );
}
